import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Hourglass, Lock, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { BRAND } from "@/brand/brand";
import type { PublicWelcome } from "../OnboardingAPI";
import { ButtonTip } from "@/components/ui/button-tip";
import { sessionFlag, usePrefersReducedMotion, useStaging } from "./calm";
import { firstNameOf } from "./firstName";
import HairlineFigure from "./HairlineFigure";

const INTRO_MS = 2600;

/** The opening: a building draws itself in hairlines, then the greeting. Once per session, skippable by click or key. */
function IntroLines({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = window.setTimeout(onDone, INTRO_MS);
    const key = () => onDone();
    window.addEventListener("keydown", key);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", key);
    };
  }, [onDone]);
  const line = (d: string, delay: number) => <path d={d} pathLength={1} className="onb-line-play" style={{ ["--onb-delay" as string]: `${delay}ms`, animationDuration: "1400ms" }} />;
  return (
    <button
      type="button"
      onClick={onDone}
      data-testid="intro"
      aria-label={t("onboardingPage.welcome.skipIntro")}
      className="relative flex min-h-[460px] w-full flex-col items-center justify-center gap-6 rounded-3xl text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <svg viewBox="0 0 320 240" className="h-auto w-full max-w-md" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {line("M40 206h240", 0)}
        {line("M80 206V92l80-44 80 44v114", 200)}
        {line("M80 148h160M80 120h160M80 176h160", 600)}
        {line("M120 206v-30M160 206v-58M200 206v-30", 900)}
        {line("M120 92h12M188 92h12", 1100)}
      </svg>
      <span className="text-xs text-muted-foreground">{t("onboardingPage.welcome.skipIntro")}</span>
    </button>
  );
}

function Trust({ icon: Icon, text, delay }: { icon: typeof Lock; text: string; delay: number }) {
  return (
    <li className="onb-rise flex items-center gap-2.5 text-sm text-muted-foreground" style={{ ["--onb-delay" as string]: `${delay}ms` }}>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      {text}
    </li>
  );
}

/**
 * The landing page: the whole stage, no card. A personal greeting with the first name (never a
 * salutation, never a guess), one sentence about what happens, the sender's own message if the
 * invitation carries one, three honest reassurances, one button - and the house, which answers the
 * pointer.
 */
export default function WelcomeScene({
  token,
  contactName,
  company,
  expiresAt,
  welcome,
  resumeAt,
  onStart,
}: {
  token: string;
  contactName: string;
  company: string;
  expiresAt?: string;
  welcome?: PublicWelcome;
  /** Set when the invitation has been worked on before: the title of the step to continue at. */
  resumeAt?: string;
  onStart: () => void;
}) {
  const { t, i18n } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const show = useStaging();
  const introFlag = sessionFlag(`onboarding.intro.${token}`);
  const [intro, setIntro] = useState(() => show && !introFlag.seen());
  const stage = useRef<HTMLDivElement>(null);
  const first = firstNameOf(contactName);
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const expires = expiresAt ? new Date(expiresAt) : null;
  const expiresText = expires && !Number.isNaN(expires.getTime()) ? new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(expires) : "";

  const finishIntro = () => {
    introFlag.mark();
    setIntro(false);
  };
  // turning the show off while the intro plays ends it
  useEffect(() => {
    if (!show && intro) finishIntro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // a very small parallax: the figure drifts a few pixels against the pointer
  const move = (event: React.PointerEvent) => {
    const node = stage.current;
    if (!node || reduced || event.pointerType !== "mouse") return;
    const box = node.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width - 0.5) * -10;
    const y = ((event.clientY - box.top) / box.height - 0.5) * -8;
    node.style.setProperty("--px", `${x.toFixed(1)}px`);
    node.style.setProperty("--py", `${y.toFixed(1)}px`);
  };

  if (intro) return <IntroLines onDone={finishIntro} />;

  const message = welcome?.message?.trim();
  const sender = welcome?.senderName?.trim();
  return (
    <div className="onb-lit relative mx-auto grid min-h-[520px] w-full items-center gap-8 grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-12" data-testid="welcome-scene" onPointerMove={move}>
      <div className="min-w-0 space-y-5 [overflow-wrap:anywhere]">
        <h1 tabIndex={-1} data-step-heading className="onb-rise text-4xl font-semibold leading-tight tracking-tight outline-none sm:text-5xl">
          {first ? t("onboardingPage.welcome.hello", { name: first }) : t("onboardingPage.welcome.helloAnonymous")}
        </h1>
        <p className="onb-rise text-xl font-medium text-foreground/80" style={{ ["--onb-delay" as string]: "70ms" }}>
          {t("onboardingPage.welcome.tagline")}
        </p>
        <p className="onb-rise text-base leading-7 text-muted-foreground" style={{ ["--onb-delay" as string]: "140ms" }}>
          {company ? t("onboardingPage.welcome.lead", { company }) : t("onboardingPage.welcome.leadAnonymous")}{" "}
          {t("onboardingPage.welcome.what", { brandName: BRAND.name })}
        </p>

        {message && (
          <blockquote className="onb-rise rounded-2xl border border-border bg-card/70 p-4 text-sm leading-6" style={{ ["--onb-delay" as string]: "210ms" }} data-testid="welcome-message">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("onboardingPage.welcome.messageTitle")}</p>
            <p className="mt-1 whitespace-pre-wrap">{message}</p>
            {sender && <footer className="mt-2 text-muted-foreground">{t("onboardingPage.welcome.messageFrom", { name: sender })}</footer>}
          </blockquote>
        )}

        <ul className="space-y-2">
          <Trust icon={Lock} text={t("onboardingPage.trust.confidential")} delay={280} />
          <Trust icon={Hourglass} text={t("onboardingPage.trust.pause")} delay={340} />
          <Trust icon={Sparkles} text={t("onboardingPage.trust.gaps")} delay={400} />
        </ul>

        <div className="onb-rise flex flex-wrap items-center gap-x-5 gap-y-2 pt-1" style={{ ["--onb-delay" as string]: "460ms" }}>
          <ButtonTip label={resumeAt ? t("onboardingPage.tips.continueAt") : t("onboardingPage.tips.letsGo")}>
            <Button type="button" size="lg" data-primary onClick={onStart} className="min-w-44">
              {resumeAt ? t("onboardingPage.welcome.continueAt", { step: resumeAt }) : t("onboardingPage.nav.letsGo")}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </ButtonTip>
          <span className="text-xs text-muted-foreground">{t("onboardingPage.nav.enterHint")}</span>
        </div>
        {resumeAt && (
          <p className="onb-rise text-sm text-muted-foreground" style={{ ["--onb-delay" as string]: "520ms" }} data-testid="welcome-back">
            {first ? t("onboardingPage.welcome.back", { name: first, step: resumeAt }) : t("onboardingPage.welcome.backAnonymous", { step: resumeAt })}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t("onboardingPage.welcome.autosave")}
          {expiresText && <> {t("onboardingPage.welcome.validUntil", { date: expiresText })}</>}
        </p>
      </div>
      <div ref={stage} className="relative mx-auto w-full max-w-[520px] transition-transform duration-500 ease-out" style={{ transform: "translate3d(var(--px, 0px), var(--py, 0px), 0)" }}>
        <HairlineFigure name="house" loop={false} />
      </div>
    </div>
  );
}
